import {
  Button,
  CanonicalFileDropzone,
  CanonicalModal,
  MoneyValue,
  SearchSelectField,
  StatusBadge,
  TextAreaField,
} from '@urbanik/ui';
import { useEffect, useState } from 'react';
import {
  getAvailablePaymentReceipts,
  linkExistingPaymentReceipt,
  reversePayment,
  uploadMemberPaymentReceipt,
  type AvailablePaymentReceipt,
  type PaymentReversalResult,
} from '@urbanik/api-client';
import { apiBase } from './api-base';
import { Download, Eye, Share2 } from 'lucide-react';
import { openProtectedDocument } from './protected-document';
import { sharePaymentReceiptViaWhatsApp } from './payment-receipt-sharing';

export type PaymentApplicationDetail = {
  id: string;
  sourceReference: string;
  amount: string;
  appliedAmount: string;
  credit: string;
  transactionDate: string | null;
  registeredAt: string | null;
  methodName: string | null;
  originBank: string | null;
  destinationAccount: string | null;
  confirmedAt: string | null;
  receiptNumber: string | null;
  registeredBy: string | null;
  approvedBy: string | null;
  status?: string;
  sourceType?: 'historical' | 'registered';
  evidenceAvailable?: boolean;
  applications: readonly { period: string; conceptName: string; amount: string; status: string }[];
};
const date = (value: string | null, withTime = false) =>
  value
    ? new Intl.DateTimeFormat('es-EC', {
        day: 'numeric',
        month: 'long',
        year: 'numeric',
        ...(withTime ? { hour: '2-digit', minute: '2-digit' } : {}),
        timeZone: 'America/Guayaquil',
      }).format(new Date(value.includes('T') ? value : `${value}T12:00:00-05:00`))
    : 'No disponible';
export function PaymentApplicationDetailModal({
  payment,
  onClose,
  evidenceUrl,
  receiptUrl,
  viewer,
  onReversed,
  memberId,
  onReceiptChanged,
}: {
  payment: PaymentApplicationDetail | null;
  onClose: () => void;
  evidenceUrl: (id: string) => string;
  receiptUrl: (id: string) => string | null;
  viewer: 'administrador' | 'socio';
  onReversed?: (result: PaymentReversalResult) => void;
  memberId?: string;
  onReceiptChanged?: () => void;
}) {
  const [reverseOpen, setReverseOpen] = useState(false),
    [reason, setReason] = useState(''),
    [busy, setBusy] = useState(false),
    [reverseError, setReverseError] = useState(''),
    [documentError, setDocumentError] = useState(''),
    [result, setResult] = useState<PaymentReversalResult | null>(null),
    [availableReceipts, setAvailableReceipts] = useState<readonly AvailablePaymentReceipt[]>([]),
    [selectedReceiptId, setSelectedReceiptId] = useState(''),
    [receiptFile, setReceiptFile] = useState<File | null>(null),
    [receiptReason, setReceiptReason] = useState(''),
    [receiptBusy, setReceiptBusy] = useState(false),
    [receiptMessage, setReceiptMessage] = useState(''),
    [shareBusy, setShareBusy] = useState(false),
    [shareMessage, setShareMessage] = useState('');
  useEffect(() => {
    if (!payment || !memberId || viewer !== 'administrador' || payment.sourceType !== 'historical')
      return;
    setSelectedReceiptId('');
    setReceiptFile(null);
    setReceiptReason('');
    setReceiptMessage('');
    void getAvailablePaymentReceipts(apiBase, memberId, payment.id)
      .then(setAvailableReceipts)
      .catch((error) =>
        setReceiptMessage(
          error instanceof Error ? error.message : 'No se pudieron cargar los comprobantes.',
        ),
      );
  }, [payment?.id, memberId, viewer]);
  const officialReceiptUrl = payment ? receiptUrl(payment.id) : null;
  const paid = payment?.applications.filter((item) => item.status === 'paid').length ?? 0,
    partial = payment?.applications.filter((item) => item.status === 'partially_paid').length ?? 0;
  return (
    <CanonicalModal
      open={Boolean(payment)}
      size="large"
      eyebrow={payment?.status === 'reversed' ? 'Comprobante reversado' : 'Comprobante aplicado'}
      title={payment ? `Comprobante ${payment.sourceReference}` : 'Detalle de pago'}
      description={
        viewer === 'administrador'
          ? 'Detalle administrativo y trazabilidad completa.'
          : 'Detalle de tu pago y cuotas aplicadas.'
      }
      onClose={() => {
        if (result) onReversed?.(result);
        else onClose();
      }}
    >
      {payment ? (
        <div className="payment-detail-modal">
          <dl className="payment-detail-modal__facts">
            <div>
              <dt>Valor</dt>
              <dd>
                <MoneyValue value={payment.amount} />
              </dd>
            </div>
            <div>
              <dt>Total aplicado</dt>
              <dd>
                <MoneyValue value={payment.appliedAmount} tone="positive" />
              </dd>
            </div>
            <div>
              <dt>Crédito</dt>
              <dd>
                <MoneyValue value={payment.credit} />
              </dd>
            </div>
            <div>
              <dt>Recibo</dt>
              <dd>{payment.receiptNumber ?? 'No disponible'}</dd>
            </div>
            <div className="payment-detail-modal__fact--wide">
              <dt>Fecha del comprobante</dt>
              <dd>{date(payment.transactionDate)}</dd>
            </div>
            <div className="payment-detail-modal__fact--wide">
              <dt>Registrado</dt>
              <dd>{date(payment.registeredAt, true)}</dd>
            </div>
            <div className="payment-detail-modal__fact--wide">
              <dt>Confirmado</dt>
              <dd>{date(payment.confirmedAt, true)}</dd>
            </div>
            <div className="payment-detail-modal__fact--wide">
              <dt>Método</dt>
              <dd>{payment.methodName ?? 'Registro histórico'}</dd>
            </div>
            <div className="payment-detail-modal__fact--wide">
              <dt>Banco de origen</dt>
              <dd>{payment.originBank ?? 'No aplica'}</dd>
            </div>
            <div className="payment-detail-modal__fact--wide">
              <dt>Cuenta receptora</dt>
              <dd>{payment.destinationAccount ?? 'No disponible'}</dd>
            </div>
            <div className="payment-detail-modal__fact--wide">
              <dt>Registrado por</dt>
              <dd>{payment.registeredBy ?? 'Proceso histórico'}</dd>
            </div>
            <div className="payment-detail-modal__fact--wide">
              <dt>Aprobado por</dt>
              <dd>{payment.approvedBy ?? 'Proceso histórico'}</dd>
            </div>
          </dl>
          <p className="payment-detail-modal__summary">
            <strong>{paid} cuotas pagadas</strong>
            {partial ? ` · ${partial} abono parcial` : ''}
            {Number(payment.credit) > 0 ? ` · Crédito ${payment.credit}` : ''}
          </p>
          <div className="payment-detail-modal__table payment-detail-modal__applications">
            <table>
              <thead>
                <tr>
                  <th>Período</th>
                  <th>Concepto</th>
                  <th>Aplicado</th>
                  <th>Resultado</th>
                </tr>
              </thead>
              <tbody>
                {payment.applications.map((item, index) => (
                  <tr key={`${item.period}-${index}`}>
                    <td data-label="Período">{item.period}</td>
                    <td data-label="Concepto">{item.conceptName}</td>
                    <td>
                      <MoneyValue value={item.amount} />
                    </td>
                    <td>
                      <StatusBadge tone={item.status === 'paid' ? 'success' : 'warning'}>
                        {item.status === 'paid' ? 'Pago completo' : 'Abono parcial'}
                      </StatusBadge>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {reverseError ? (
            <p className="canonical-notice canonical-notice--error" role="alert">
              {reverseError}
            </p>
          ) : null}
          {result ? (
            <section className="payment-detail-modal__table payment-reversal-comparison">
              <h3>Comparación de saldos</h3>
              <p>
                Crédito reversado: <strong>USD {result.creditReversed}</strong>
              </p>
              <table>
                <thead>
                  <tr>
                    <th>Período</th>
                    <th>Antes</th>
                    <th>Después</th>
                  </tr>
                </thead>
                <tbody>
                  {result.after.map((row, index) => (
                    <tr key={row.period}>
                      <td>{row.period}</td>
                      <td>
                        Aplicado USD {result.before[index]?.appliedAmount} · Pendiente USD{' '}
                        {result.before[index]?.outstandingAmount}
                      </td>
                      <td>
                        Aplicado USD {row.appliedAmount} · Pendiente USD {row.outstandingAmount}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </section>
          ) : null}
          {viewer === 'administrador' && memberId && payment.sourceType === 'historical' ? (
            <section className="ui-form-stack payment-detail-modal__receipt-manager">
              <h3>Comprobante de la cuota importada</h3>
              <p>
                Adjunta un JPG, PNG o PDF nuevo, o vincula un comprobante que ya esté registrado en
                esta entidad.
              </p>
              <CanonicalFileDropzone
                label="Subir comprobante nuevo"
                file={receiptFile}
                onFileChange={setReceiptFile}
                onError={setReceiptMessage}
                accept="image/jpeg,image/png,application/pdf,.jpg,.jpeg,.png,.pdf"
                helpText="JPG, PNG o PDF · máximo 20 MB."
                allowCamera
              />
              <SearchSelectField
                label="Vincular comprobante existente"
                value={selectedReceiptId}
                onValueChange={setSelectedReceiptId}
                placeholder="Buscar por archivo o referencia"
                emptyMessage="No hay otros comprobantes registrados en esta entidad."
                options={availableReceipts.map((receipt) => ({
                  value: receipt.id,
                  label: receipt.fileName,
                  meta: [
                    receipt.sourceReference,
                    receipt.sourceSheet,
                    receipt.linked ? 'Ya vinculado' : '',
                  ]
                    .filter(Boolean)
                    .join(' · '),
                }))}
              />
              <TextAreaField
                label="Motivo del vínculo o carga"
                required
                value={receiptReason}
                onChange={(event) => setReceiptReason(event.target.value)}
                hint="Mínimo 10 caracteres; esta explicación queda en auditoría."
              />
              {receiptMessage ? (
                <p className="ui-alert" role="status">
                  {receiptMessage}
                </p>
              ) : null}
              <div className="ui-form-actions">
                <Button
                  variant="secondary"
                  disabled={receiptBusy || !selectedReceiptId || receiptReason.trim().length < 10}
                  onClick={async () => {
                    setReceiptBusy(true);
                    setReceiptMessage('');
                    try {
                      await linkExistingPaymentReceipt(
                        apiBase,
                        memberId,
                        payment.id,
                        selectedReceiptId,
                        receiptReason,
                      );
                      setReceiptMessage('Comprobante existente vinculado correctamente.');
                      onReceiptChanged?.();
                    } catch (error) {
                      setReceiptMessage(
                        error instanceof Error ? error.message : 'No se pudo vincular.',
                      );
                    } finally {
                      setReceiptBusy(false);
                    }
                  }}
                >
                  Vincular existente
                </Button>
                <Button
                  disabled={receiptBusy || !receiptFile || receiptReason.trim().length < 10}
                  onClick={async () => {
                    if (!receiptFile) return;
                    setReceiptBusy(true);
                    setReceiptMessage('');
                    try {
                      await uploadMemberPaymentReceipt(
                        apiBase,
                        memberId,
                        payment.id,
                        receiptFile,
                        receiptReason,
                      );
                      setReceiptFile(null);
                      setReceiptMessage('Comprobante cargado y vinculado correctamente.');
                      setAvailableReceipts(
                        await getAvailablePaymentReceipts(apiBase, memberId, payment.id),
                      );
                      onReceiptChanged?.();
                    } catch (error) {
                      setReceiptMessage(
                        error instanceof Error ? error.message : 'No se pudo cargar.',
                      );
                    } finally {
                      setReceiptBusy(false);
                    }
                  }}
                >
                  {receiptBusy ? 'Guardando…' : 'Subir y vincular'}
                </Button>
              </div>
            </section>
          ) : null}
          {documentError ? (
            <div className="ui-alert" role="alert">
              {documentError}
            </div>
          ) : null}
          {shareMessage ? (
            <div className="ui-alert ui-alert--success" role="status">
              {shareMessage}
            </div>
          ) : null}
          <div className="manual-allocation__actions">
            {!result && payment.status !== 'reversed' && payment.evidenceAvailable !== false ? (
              <button
                type="button"
                className="ui-button ui-button--secondary"
                onClick={() => {
                  setDocumentError('');
                  void openProtectedDocument(evidenceUrl(payment.id), 'preview').catch((error) =>
                    setDocumentError(
                      error instanceof Error ? error.message : 'No se pudo abrir la evidencia.',
                    ),
                  );
                }}
              >
                <Eye aria-hidden="true" /> Ver evidencia
              </button>
            ) : null}
            {viewer === 'administrador' &&
            payment.sourceType === 'registered' &&
            payment.status !== 'reversed' &&
            !result ? (
              <button
                type="button"
                className="ui-button ui-button--danger"
                onClick={() => setReverseOpen(true)}
              >
                Reversar comprobante
              </button>
            ) : null}
            {officialReceiptUrl && !result && payment.status !== 'reversed' ? (
              <button
                type="button"
                className="ui-button ui-button--secondary"
                disabled={shareBusy}
                onClick={() => {
                  setDocumentError('');
                  setShareMessage('');
                  setShareBusy(true);
                  void sharePaymentReceiptViaWhatsApp({
                    url: officialReceiptUrl,
                    receiptNumber: payment.receiptNumber ?? payment.sourceReference,
                    appliedAmount: payment.appliedAmount,
                  })
                    .then((shareResult) =>
                      setShareMessage(
                        shareResult === 'copied-and-opened'
                          ? 'Imagen copiada. Selecciona el chat de WhatsApp y pega con Ctrl+V.'
                          : shareResult === 'downloaded-and-opened'
                            ? 'WhatsApp abierto con el texto. Adjunta el PNG descargado.'
                            : 'Recibo compartido con el selector del sistema.',
                      ),
                    )
                    .catch((error) =>
                      setDocumentError(
                        error instanceof Error ? error.message : 'No se pudo compartir el recibo.',
                      ),
                    )
                    .finally(() => setShareBusy(false));
                }}
              >
                <Share2 aria-hidden="true" />{' '}
                {shareBusy ? 'Preparando\u2026' : 'Enviar por WhatsApp'}
              </button>
            ) : null}
            {officialReceiptUrl && !result && payment.status !== 'reversed' ? (
              <button
                type="button"
                className="ui-button ui-button--primary"
                onClick={() => {
                  setDocumentError('');
                  void openProtectedDocument(officialReceiptUrl, 'download', 'recibo.png').catch(
                    (error) =>
                      setDocumentError(
                        error instanceof Error ? error.message : 'No se pudo descargar el recibo.',
                      ),
                  );
                }}
              >
                <Download aria-hidden="true" /> Descargar recibo oficial
              </button>
            ) : null}
          </div>
        </div>
      ) : null}
      <CanonicalModal
        open={reverseOpen}
        size="medium"
        eyebrow="Corrección financiera"
        title="Reversar comprobante aplicado"
        description="Se revertirán las aplicaciones y el crédito. La imagen o PDF original se eliminará permanentemente; solo permanecerá la trazabilidad mínima auditada."
        onClose={() => !busy && setReverseOpen(false)}
      >
        <div className="payment-reversal-form">
          <label className="member-payment__field">
            <span>Motivo del reverso *</span>
            <textarea
              rows={4}
              minLength={10}
              value={reason}
              onChange={(event) => setReason(event.target.value)}
            />
            <small>
              Describe claramente el error y la corrección requerida. Mínimo 10 caracteres ·{' '}
              {reason.trim().length}/10
            </small>
          </label>
        </div>
        <div className="manual-allocation__actions">
          <button
            type="button"
            className="ui-button ui-button--secondary"
            disabled={busy}
            onClick={() => setReverseOpen(false)}
          >
            Cancelar
          </button>
          <button
            type="button"
            className="ui-button ui-button--danger"
            disabled={busy || reason.trim().length < 10}
            onClick={async () => {
              if (!payment) return;
              setBusy(true);
              setReverseError('');
              try {
                const response = await reversePayment(apiBase, payment.id, reason);
                setResult(response);
                setReverseOpen(false);
                onReversed?.(response);
              } catch (error) {
                setReverseError((error as Error).message);
                setReverseOpen(false);
              } finally {
                setBusy(false);
              }
            }}
          >
            {busy ? 'Reversando y eliminando…' : 'Reversar y eliminar evidencia'}
          </button>
        </div>
      </CanonicalModal>
    </CanonicalModal>
  );
}
